/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { memo } from 'react';

import { NodeProps } from '@nivo/network';
import { animated as springAnimated, to } from '@react-spring/web';

import { CIFonts } from 'shared';

import { NetworkNode } from '../NodesNetwork.types';

export const NodesNetworkCustomNode = memo<NodeProps<NetworkNode>>(props => {
  const { node, animated, ...events } = props;
  const { x, y, size, color, borderWidth, borderColor, opacity, scale } = animated;
  const { onClick, onMouseEnter, onMouseMove, onMouseLeave } = events;

  return (
    <springAnimated.g
      transform={to([x, y, scale], (vx, vy, s) => `translate(${vx}, ${vy}) scale(${s})`)}
      cursor={node.data.type === 'add' ? 'pointer' : 'default'}
      onClick={e => {
        e.preventDefault();
        onClick?.(node, e);
        if (node.data.type === 'add') {
          onMouseLeave?.(node, e);
        }
      }}
      onMouseEnter={e => {
        e.preventDefault();
        onMouseEnter?.(node, e);
      }}
      onMouseMove={e => {
        e.preventDefault();
        onMouseMove?.(node, e);
      }}
      onMouseLeave={e => {
        e.preventDefault();
        onMouseLeave?.(node, e);
      }}
    >
      <springAnimated.circle
        r={to(size, s => s / 2)}
        fill={to(color, c => c)}
        stroke={to(borderColor, bc => bc)}
        strokeWidth={to(borderWidth, bw => bw)}
        opacity={to(opacity, o => o)}
        scale={to(scale, s => s)}
      />
      {node.data.type === 'add' ? (
        <springAnimated.svg
          x={to([size, scale], (s, sc) => -(s / 3) * sc)}
          y={to([size, scale], (s, sc) => -(s / 3) * sc)}
          width={to([size, scale], (s, sc) => (s / 1.5) * sc)}
          height={to([size, scale], (s, sc) => (s / 1.5) * sc)}
          viewBox="0 0 24 24"
          fill="none"
          style={{ pointerEvents: 'none' }}
        >
          <line x1="12" y1="5" x2="12" y2="19" stroke="white" strokeWidth="3" strokeLinecap="round" />
          <line x1="5" y1="12" x2="19" y2="12" stroke="white" strokeWidth="3" strokeLinecap="round" />
        </springAnimated.svg>
      ) : (
        <springAnimated.text
          x={0}
          y={0}
          style={{
            transform: to(scale, sc => `scale(${sc})`),
          }}
          textAnchor="middle"
          dominantBaseline="central"
          fill="white"
          fontSize={to(size, s => s / 2.2)}
          fontFamily={CIFonts.Text}
          fontWeight={700}
          cursor="default"
          pointerEvents="none"
        >
          {node.id}
        </springAnimated.text>
      )}
    </springAnimated.g>
  );
});
